import { normalizeSearchName } from '../utils/normalizeSearchName.js';
/**
 * The 27 federative units.
 *
 * This is `…/localidades/estados` frozen: 2,4 KB that have not changed since
 * 1988. It is **not** a duplicate of what the API gives — it is what makes the
 * state combobox open instantly and keep working with the IBGE down (D28),
 * which preserves half the address flow in any outage. Only the municipality
 * list goes to the network.
 *
 * Ordered by `name` with `localeCompare('pt-BR')` — accents and all — so the
 * combobox reads alphabetically without sorting at render. It used to be ordered
 * by `uf`, which was right while the option label started with the abbreviation
 * (`SP — São Paulo`); request 0261 dropped the abbreviation from the label, and
 * UF order then looked random on screen (`Amapá` before `Amazonas`, `Bahia`
 * after `Amazonas` but before `Ceará`). `brazilStates.test.ts` holds the order.
 */
export const BRAZIL_STATES = [
    { id: '12', uf: 'AC', name: 'Acre', region: 'N' },
    { id: '27', uf: 'AL', name: 'Alagoas', region: 'NE' },
    { id: '16', uf: 'AP', name: 'Amapá', region: 'N' },
    { id: '13', uf: 'AM', name: 'Amazonas', region: 'N' },
    { id: '29', uf: 'BA', name: 'Bahia', region: 'NE' },
    { id: '23', uf: 'CE', name: 'Ceará', region: 'NE' },
    { id: '53', uf: 'DF', name: 'Distrito Federal', region: 'CO' },
    { id: '32', uf: 'ES', name: 'Espírito Santo', region: 'SE' },
    { id: '52', uf: 'GO', name: 'Goiás', region: 'CO' },
    { id: '21', uf: 'MA', name: 'Maranhão', region: 'NE' },
    { id: '51', uf: 'MT', name: 'Mato Grosso', region: 'CO' },
    { id: '50', uf: 'MS', name: 'Mato Grosso do Sul', region: 'CO' },
    { id: '31', uf: 'MG', name: 'Minas Gerais', region: 'SE' },
    { id: '15', uf: 'PA', name: 'Pará', region: 'N' },
    { id: '25', uf: 'PB', name: 'Paraíba', region: 'NE' },
    { id: '41', uf: 'PR', name: 'Paraná', region: 'S' },
    { id: '26', uf: 'PE', name: 'Pernambuco', region: 'NE' },
    { id: '22', uf: 'PI', name: 'Piauí', region: 'NE' },
    { id: '33', uf: 'RJ', name: 'Rio de Janeiro', region: 'SE' },
    { id: '24', uf: 'RN', name: 'Rio Grande do Norte', region: 'NE' },
    { id: '43', uf: 'RS', name: 'Rio Grande do Sul', region: 'S' },
    { id: '11', uf: 'RO', name: 'Rondônia', region: 'N' },
    { id: '14', uf: 'RR', name: 'Roraima', region: 'N' },
    { id: '42', uf: 'SC', name: 'Santa Catarina', region: 'S' },
    { id: '35', uf: 'SP', name: 'São Paulo', region: 'SE' },
    { id: '28', uf: 'SE', name: 'Sergipe', region: 'NE' },
    { id: '17', uf: 'TO', name: 'Tocantins', region: 'N' },
];
const BY_UF = new Map(BRAZIL_STATES.map((state) => [state.uf, state]));
const BY_ID = new Map(BRAZIL_STATES.map((state) => [state.id, state]));
/** The UF by its two-letter abbreviation (case-insensitive), or null. */
export function brazilStateByUf(uf) {
    if (!uf)
        return null;
    return BY_UF.get(uf.trim().toUpperCase()) ?? null;
}
/**
 * The UF by its IBGE code, or null. Accepts the code as a number too: the IBGE
 * answers `id: 35` and the ViaCEP answers `"35"` — a `===` between the two is
 * always false, with a green typecheck (D29).
 */
export function brazilStateById(id) {
    if (id == null || id === '')
        return null;
    return BY_ID.get(String(id).trim().padStart(2, '0')) ?? null;
}
/**
 * Spelled-out macro-region → our `StateRegion` code.
 *
 * Keyed by `normalizeSearchName`, so casing and accents do not matter. The
 * ViaCEP answers `regiao: "Sudeste"` and the enum is what every descriptor
 * declares — the text must never be passed through.
 */
const REGION_BY_NORMALIZED_NAME = {
    norte: 'N',
    nordeste: 'NE',
    'centro-oeste': 'CO',
    sudeste: 'SE',
    sul: 'S',
};
/** `'Centro-Oeste'` → `'CO'`. Anything outside the five macro-regions → null. */
export function stateRegionFromName(value) {
    if (!value)
        return null;
    return REGION_BY_NORMALIZED_NAME[normalizeSearchName(value)] ?? null;
}
//# sourceMappingURL=brazilStates.js.map